// Lazily loads Google's Identity Services SDK (the "Sign in with Google"
// button + credential flow) and caches the load so multiple mounts of
// GoogleSignInButton don't inject the script twice.

// hl=en pins the rendered button/dialog text to English regardless of the
// browser's detected locale — every other string in this app is English-only.
const SCRIPT_SRC = "https://accounts.google.com/gsi/client?hl=en";

let loadPromise = null;

// The FedCM/native-button credential (unlike the plain-OAuth id_token used by
// the redirect fallback — see googleAuthRedirect.js) reliably carries profile
// claims, so the name can be read straight off it without a userinfo round
// trip. Best-effort: only used to prefill the onboarding name field.
export function decodeGoogleCredentialName(idToken) {
  try {
    const payload = idToken.split(".")[1];
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/").padEnd(payload.length + ((4 - (payload.length % 4)) % 4), "=");
    const json = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + c.charCodeAt(0).toString(16).padStart(2, "0"))
        .join(""),
    );
    const claims = JSON.parse(json);
    return typeof claims.name === "string" ? claims.name : null;
  } catch {
    return null;
  }
}

export function loadGoogleIdentityServices() {
  if (window.google?.accounts?.id) {
    return Promise.resolve(window.google);
  }
  if (!loadPromise) {
    loadPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
      if (existing) {
        existing.addEventListener("load", () => resolve(window.google));
        existing.addEventListener("error", () => reject(new Error("Failed to load Google Identity Services")));
        return;
      }
      const script = document.createElement("script");
      script.src = SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve(window.google);
      script.onerror = () => reject(new Error("Failed to load Google Identity Services"));
      document.head.appendChild(script);
    });
  }
  return loadPromise;
}
